import { buildCanonicalProductPath } from '../../../routing'
import { fetchCatalogCategories, fetchProductById, fetchProductByPublicCode, fetchProductBySlug } from '../catalogApi'
import { replaceLocalizedTargetIfNeeded } from './navigation'
import type { LoadedRouteContentData, ProductRoute, RouteContentLoaderLocaleContext } from './types'

/**
 * Load product route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const loadProductRouteContent = async ({
  route,
  signal,
  localeContext,
}: {
  route: ProductRoute
  signal: AbortSignal
  localeContext: RouteContentLoaderLocaleContext
}): Promise<LoadedRouteContentData> => {
  const product = route.previewId
    ? await fetchProductById(route.previewId, signal)
    : route.publicCode
    ? await fetchProductByPublicCode(route.publicCode, signal)
    : await fetchProductBySlug(route.slug, signal)

  const categories = await fetchCatalogCategories(signal).catch(() => [])
  const canonicalPath = buildCanonicalProductPath(product, categories)
  const canonicalTarget = route.previewId
    ? `${canonicalPath}?preview_id=${encodeURIComponent(route.previewId)}`
    : canonicalPath
  replaceLocalizedTargetIfNeeded({
    nextTarget: canonicalTarget,
    toLocalizedCanonical: localeContext.toLocalizedCanonical,
  })

  return { type: 'product', product }
}
